/**
 * The report on its own page — the target of the shareable link.
 *
 * Rule 29a asks for "an org-scoped URL to a workstream's report". The first
 * implementation pointed that link at `/workstream/:id?plan_version=N`, which
 * pins the version but still renders the whole workstream: plan panel, step
 * rail, and the report nested inside the analysis step's card. A recipient
 * handed that link has to find the report inside it.
 *
 * This route renders the report and nothing else. It is deliberately thin:
 * the report has its own endpoint, so no plan, steps or SSE stream is needed
 * here. `ReportView` is imported rather than reimplemented so the standalone
 * page and the in-page render can never drift apart.
 *
 * Authorization is unchanged: `ProtectedRoute` in the app, the existing
 * org predicate on the route (`agent_workstream_routes.py:205-212`). No
 * token, no public access, no expiry, no new storage — decisions 8 and 9
 * are untouched.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { apiGet, ReportView } from './WorkstreamPage';
import type { WorkstreamReport } from '../types/workstream';

export default function WorkstreamReportPage() {
  const { id } = useParams<{ id: string }>();
  const workstreamId = id ?? null;

  // Rule 29b: the expected plan version rides the URL and is read from it
  // directly — never inferred from the workstream's current approval, because
  // an echoed argument is not provenance. Parsed exactly as WorkstreamPage
  // parses it, so the two routes agree on what a valid identifier is.
  const [searchParams] = useSearchParams();
  const requestedVersionParam = searchParams.get('plan_version');
  const requestedVersion =
    requestedVersionParam !== null &&
    requestedVersionParam.trim() !== '' &&
    Number.isFinite(Number(requestedVersionParam))
      ? Number(requestedVersionParam)
      : null;

  const [report, setReport] = useState<WorkstreamReport | null>(null);
  const [versionUnavailable, setVersionUnavailable] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const loadReport = useCallback(async () => {
    if (!workstreamId) return;
    setLoading(true);
    const qs = requestedVersion != null ? `?plan_version=${requestedVersion}` : '';
    const res = await apiGet<WorkstreamReport>(`/v2/workstreams/${workstreamId}/report${qs}`);
    if (res.ok && res.body) {
      setReport(res.body);
      setVersionUnavailable(null);
    } else {
      // Rule 29d: the current report must never be rendered under a
      // non-matching identifier. Clear it rather than leave it standing.
      setReport(null);
      setVersionUnavailable(requestedVersion);
    }
    setLoading(false);
  }, [workstreamId, requestedVersion]);

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 space-y-4" data-testid="standalone-report">
      <Link
        to={`/workstream/${workstreamId ?? ''}`}
        className="inline-block text-sm text-indigo-700 underline underline-offset-2 print:hidden"
        data-testid="back-to-workstream"
      >
        ← Back to the full workstream
      </Link>

      {loading ? (
        <p className="text-sm text-slate-600" data-testid="standalone-report-loading">
          Loading report…
        </p>
      ) : report ? (
        <ReportView report={report} />
      ) : versionUnavailable !== null ? (
        <p role="alert" className="text-sm text-amber-900" data-testid="report-version-unavailable">
          The report for the requested version ({versionUnavailable}) is unavailable.
        </p>
      ) : (
        <p role="alert" className="text-sm text-amber-900" data-testid="standalone-report-unavailable">
          This report is unavailable.
        </p>
      )}
    </div>
  );
}
